import type { UseMutationOptions } from "@tanstack/react-query"
import type { AxiosError } from "axios"

import { queryClient } from "~/backend/queryClient"
import type {
  CreateFaqRequestDto,
  CreateFaqResponseDto,
  UpdateFaqRequestDto,
  UpdateFaqResponseDto,
} from "~/gen/api"
import {
  findFaqByLanguageQueryKey,
  useCreateFaq,
  useUpdateFaq,
} from "~/gen/api"

export function useCreateFAQMutation(
  options?: UseMutationOptions<
    CreateFaqResponseDto,
    AxiosError,
    CreateFaqRequestDto
  >
) {
  const createFaqMutation = useCreateFaq({
    mutation: {
      onSuccess: async (response, variables, context) => {
        await queryClient.invalidateQueries({
          queryKey: findFaqByLanguageQueryKey(variables.data.lang),
        })
        options?.onSuccess?.(
          response.data,
          variables.data,
          context as never,
          undefined as never
        )
      },
    },
  })

  return {
    ...createFaqMutation,
    mutateAsync: (variables: CreateFaqRequestDto) =>
      createFaqMutation
        .mutateAsync({ data: variables })
        .then((res) => res.data),
  }
}

export function useEditFAQMutation(
  faqId: string | undefined,
  options?: UseMutationOptions<
    UpdateFaqResponseDto,
    AxiosError,
    UpdateFaqRequestDto
  >
) {
  const updateFaqMutation = useUpdateFaq({
    mutation: {
      onSuccess: async (response, variables, context) => {
        await queryClient.invalidateQueries({
          queryKey: findFaqByLanguageQueryKey(variables.data.lang),
        })
        options?.onSuccess?.(
          response.data,
          variables.data,
          context as never,
          undefined as never
        )
      },
    },
  })

  return {
    ...updateFaqMutation,
    mutateAsync: (variables: UpdateFaqRequestDto) =>
      faqId !== undefined
        ? updateFaqMutation
            .mutateAsync({ id: faqId, data: variables })
            .then((res) => res.data)
        : Promise.reject(new Error("FAQ id is required for update")),
  }
}
